import { Fragment } from 'react';

import { splitParagraphs } from '../lib/format';
import { InlineMarkdown } from './InlineMarkdown';

/**
 * Authored prose, split on blank lines into paragraphs with single newlines kept
 * as line breaks, and each line's inline markdown rendered.
 *
 * The two halves answer different problems and neither replaces the other. The
 * blank lines are the pipeline's own paragraph breaks — rendering the text in
 * one `<p>` collapses every one of them — and the block structure is all the
 * markdown these fields carry. Inside a line, `**bold**` and `[label](url)` are
 * the enrichment's only tokens, and left alone they reach the reviewer as
 * literal asterisks and raw URLs.
 *
 * Running the inline parse over text that was never meant as markdown is safe:
 * both tokens require a closing half, so a lone asterisk in a claim is text.
 *
 * `gr-paras` carries the paragraph spacing: Tailwind's preflight zeroes `<p>`
 * margins app-wide, so without it every paragraph would butt against the next.
 */
export function Prose({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const paragraphs = splitParagraphs(text);

  return (
    <div className={className ? `gr-paras ${className}` : 'gr-paras'}>
      {paragraphs.map((paragraph, paragraphIndex) => (
        <p key={`${paragraphIndex}-${paragraph.slice(0, 32)}`}>
          {paragraph.split('\n').map((line, lineIndex) => (
            <Fragment key={`${lineIndex}-${line.slice(0, 32)}`}>
              {lineIndex > 0 && <br />}
              <InlineMarkdown text={line} />
            </Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}
